import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AvatarChatService from './chatSDK/chat.js';
import './AvatarChat.css';
import Sidebar2 from '../../Sidebars/slider_panel/sidebar2.jsx';

const ConversationSession = () => {
  // State variables
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', isError: false });
  const [chatOpen, setChatOpen] = useState(false);
  const [callInitiated, setCallInitiated] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const navigate = useNavigate();

  // Refs
  const chatServiceRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const subtitlesRef = useRef(null);

  // Functions
  const loadBackgroundImage = (imageUrl) => {
    if (!imageUrl || imageUrl.includes('placeholder')) {
      setBackgroundLoaded(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      console.log("✅ Background image loaded successfully");
      setBackgroundLoaded(true);
    };
    img.onerror = () => {
      console.log("❌ Background image failed to load:", imageUrl);
      setBackgroundLoaded(false);
    };
    img.src = imageUrl;
  };

  // Display status messages with auto-fade
  const showStatus = (message, isError = false) => {
    setStatusMessage({ text: message, isError });
    setTimeout(() => {
      setStatusMessage({ text: '', isError: false });
    }, 3000);
  };

  // Start avatar session
  const startSession = () => {
    if (!sessionData) {
      showStatus('No session data available', true);
      return;
    }
    
    setLoading(true);
    
    // Combine system prompt with user scenario if provided
    const fullPrompt = sessionData.userScenario 
      ? `${sessionData.systemPrompt}\n\nCurrent scenario: ${sessionData.userScenario}`
      : sessionData.systemPrompt;
    
    // Update the config with the current system prompt
    const sessionConfig = {
      ...sessionData.config,
      systemPrompt: fullPrompt
    };
    
    // Connect to avatar service
    chatServiceRef.current.connectAvatar(sessionConfig);
    
    // Set up initial message if provided
    if (sessionData.initialMessage) {
      setCallInitiated(true);
      setUserInput(sessionData.initialMessage);
    }
  };

  // Disconnect from avatar service
  const endSession = () => {
    if (chatServiceRef.current) {
      chatServiceRef.current.disconnectAvatar();
    }
    setChatOpen(false);
    setMessages([]);
    
    // Clear session data and navigate back
    sessionStorage.removeItem('pendingAvatarSession');
    navigate('/chat');
  };

  // Handle sending a message
  const sendMessage = () => {
    if (!userInput.trim() || !connected || !sessionData) return;
    
    chatServiceRef.current.handleUserQuery(userInput, userInput, "", sessionData.config);
    setMessages(prev => [...prev, { role: 'user', content: userInput, isComplete: true }]);
    setUserInput('');
  };

  // Handle microphone toggle
  const toggleMicrophone = () => {
    if (!sessionData) return;
    
    if (micActive) {
      chatServiceRef.current.stopMicrophone()
        .then(() => setMicActive(false))
        .catch(error => showStatus(`Microphone error: ${error}`, true));
    } else {
      chatServiceRef.current.startMicrophone(sessionData.config)
        .then(() => setMicActive(true))
        .catch(error => showStatus(`Microphone error: ${error}`, true));
    }
  };

  // Clear chat history
  const clearChat = () => {
    if (chatServiceRef.current && sessionData) {
      const fullPrompt = sessionData.userScenario 
        ? `${sessionData.systemPrompt}\n\nCurrent scenario: ${sessionData.userScenario}`
        : sessionData.systemPrompt;
      
      chatServiceRef.current.clearChatHistory(fullPrompt);
      setMessages([]);
    }
  };

  // Stop the avatar from speaking
  const stopSpeaking = () => {
    if (chatServiceRef.current && speaking) {
      chatServiceRef.current.stopSpeaking();
    }
  };

  // Handle input field changes
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  // Toggle chat visibility
  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

// Replace the useEffect in ConversationSession that loads session data with this improved version:

// Fixed useEffect for loading session data in ConversationSession.jsx
useEffect(() => {
  console.log('ConversationSession: Component mounted, checking for session data...'); // Debug log
  
  // Check for pending session data with more detailed logging
  const pendingSession = sessionStorage.getItem('pendingAvatarSession');
  console.log('Raw session data from storage:', pendingSession ? 'Data found' : 'No data'); // Debug log
  console.log('Session data length:', pendingSession?.length || 0); // Debug log
  
  if (!pendingSession) {
    console.log('No session data found in sessionStorage'); // Debug log
    
    // Check if there's any data in sessionStorage at all
    console.log('All sessionStorage keys:', Object.keys(sessionStorage)); // Debug log
    
    showStatus('No session data found. Redirecting to chat...', true);
    setTimeout(() => navigate('/chat'), 3000);
    return;
  }

  try {
    const parsedSessionData = JSON.parse(pendingSession);
    console.log('Parsed session data successfully:', {
      hasConfig: !!parsedSessionData.config,
      hasSystemPrompt: !!parsedSessionData.systemPrompt,
      hasUserScenario: !!parsedSessionData.userScenario,
      hasInitialMessage: !!parsedSessionData.initialMessage,
      timestamp: parsedSessionData.timestamp,
      debug: parsedSessionData.debug
    }); // Debug log
    
    // Validate that required data exists with more specific checks
    if (!parsedSessionData.config) {
      console.error('Missing config in session data');
      showStatus('Missing configuration data. Redirecting...', true);
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    if (!parsedSessionData.systemPrompt) {
      console.error('Missing systemPrompt in session data');
      showStatus('Missing system prompt. Redirecting...', true);
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    // Additional validation for critical config fields
    if (!parsedSessionData.config.cogSvcSubKey) {
      console.error('Missing cogSvcSubKey in config');
      showStatus('Missing Azure Speech key. Redirecting...', true);
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    if (!parsedSessionData.config.azureOpenAIEndpoint) {
      console.error('Missing azureOpenAIEndpoint in config');
      showStatus('Missing Azure OpenAI endpoint. Redirecting...', true);
      setTimeout(() => navigate('/chat'), 3000);
      return;
    }
    
    console.log('Session data validation passed, setting up session...'); // Debug log
    setSessionData(parsedSessionData);
    
    // Load background image if available
    if (parsedSessionData.config.backgroundImageUrl) {
      loadBackgroundImage(parsedSessionData.config.backgroundImageUrl);
    }
    
    showStatus('Session data loaded successfully');
    
  } catch (error) {
    console.error('Failed to parse session data:', error);
    console.error('Raw data that failed to parse:', pendingSession); // Debug log
    showStatus('Invalid session data format. Redirecting...', true);
    setTimeout(() => navigate('/chat'), 3000);
    return;
  }

  // Only proceed with chat service setup if we have valid session data
  console.log('Setting up chat service...'); // Debug log
  
  // Create service instance
  chatServiceRef.current = new AvatarChatService();
  
  // Set up event handlers
  chatServiceRef.current.onStatusChange = (message) => {
    console.log('Status change:', message); // Debug log
    showStatus(message);
  };
  
  chatServiceRef.current.onConnectionStateChange = (state) => {
    console.log('Connection state change:', state); // Debug log
    if (state === 'connected') {
      setConnected(true);
      setLoading(false);
      setChatOpen(true);
      showStatus('Connected to avatar successfully');
    } else if (state === 'disconnected') {
      setConnected(false);
      setCallInitiated(false);
    } else if (state === 'connecting') {
      setLoading(true);
      showStatus('Connecting to avatar...');
    } else if (state === 'error') {
      setLoading(false);
      showStatus('Connection failed', true);
    }
  };
  
  chatServiceRef.current.onSpeakingStateChange = (isSpeaking) => {
    setSpeaking(isSpeaking);
  };
  
  chatServiceRef.current.onMessageReceived = (message) => {
    setMessages(prevMessages => {
      if (message.role === 'assistant') {
        const updatedMessages = [...prevMessages];
        
        const lastIndex = updatedMessages.findIndex(
          (msg, idx) => msg.role === 'assistant' && !msg.isComplete && 
          idx > updatedMessages.findLastIndex(m => m.role === 'user')
        );
        
        if (lastIndex !== -1) {
          updatedMessages[lastIndex] = { 
            ...updatedMessages[lastIndex], 
            content: message.content, 
            isComplete: message.isComplete 
          };
          return updatedMessages;
        }
      }
      
      return [...prevMessages, message];
    });
  };
  
  chatServiceRef.current.onSubtitleUpdate = (text) => {
    if (subtitlesRef.current) {
      subtitlesRef.current.textContent = text;
      subtitlesRef.current.hidden = !text;
    }
  };
  
  chatServiceRef.current.onVideoConnect = (stream) => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    }
  };
  
  chatServiceRef.current.onAudioConnect = (stream) => {
    if (audioRef.current) {
      audioRef.current.srcObject = stream;
      audioRef.current.play();
    }
  };
  
  chatServiceRef.current.onError = (message) => {
    console.error('Avatar service error:', message); // Debug log
    showStatus(message, true);
  };
  
  // Health check interval
  const healthCheckInterval = setInterval(() => {
    if (chatServiceRef.current && connected) {
      const isHealthy = chatServiceRef.current.checkHealth();
      if (!isHealthy) {
        setConnected(false);
        showStatus('Avatar session timed out', true);
      }
    }
  }, 5000);
  
  // Clean up on unmount
  return () => {
    clearInterval(healthCheckInterval);
    if (chatServiceRef.current) {
      chatServiceRef.current.disconnectAvatar();
    }
  };
}, [navigate]);

// Separate useEffect to auto-start session once session data is loaded
useEffect(() => {
  if (sessionData && !connected && !loading) {
    console.log('Session data loaded, auto-starting session...'); // Debug log
    
    // Add a small delay to ensure everything is properly initialized
    setTimeout(() => {
      startSession();
    }, 1000);
  }
}, [sessionData, connected, loading]);

  // Effect to send initial message after connection
  useEffect(() => {
    if (connected && callInitiated && userInput && sessionData) {
      chatServiceRef.current.handleUserQuery(userInput, userInput, "", sessionData.config);
      setMessages(prev => [...prev, { role: 'user', content: userInput, isComplete: true }]);
      setUserInput('');
      setCallInitiated(false);
    }
  }, [connected, callInitiated, userInput, sessionData]);

  if (!sessionData) {
    return (
      <div className="avatar-chat-container">
        <div className="chat-interface">
          <div className="avatar-container">
            <div className="session-loading-overlay">
              <div className="session-loading-content">
                <div className="session-loading-spinner">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </div>
                <h2 className="session-loading-title">Loading Session...</h2>
                <p className="session-loading-subtitle">Preparing your avatar experience</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="avatar-chat-container">
      <div className="chat-interface">
        {/* Avatar Container */}
        <div 
          className="avatar-container"
          style={{
            backgroundImage: connected && backgroundLoaded && sessionData.config.backgroundImageUrl 
              ? `url(${sessionData.config.backgroundImageUrl})` 
              : 'none',
            backgroundColor: connected && backgroundLoaded && sessionData.config.backgroundImageUrl 
              ? 'transparent' 
              : '#2b2b2b',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <video 
            ref={videoRef} 
            className="avatar-video" 
            autoPlay 
            playsInline
            muted={false}
          />
          <audio ref={audioRef} autoPlay />
          <div 
            ref={subtitlesRef} 
            className="subtitles" 
            hidden
          />

          {/* Session Ready Overlay - shows after data loads but before calling */}
          {sessionData && !loading && !connected && (
            <div className="session-ready-overlay">
              <div className="session-ready-content">
                <div className="success-checkmark">
                  <svg viewBox="0 0 52 52" className="checkmark">
                    <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                    <path className="checkmark-check" fill="none" d="m14.1 27.2l7.1 7.2 16.7-16.8"/>
                  </svg>
                </div>
                <h2 className="session-ready-title">Session Ready!</h2>
                <p className="session-ready-subtitle">Data loaded successfully • Starting connection...</p>
              </div>
            </div>
          )}

          {/* Calling Overlay */}
          {loading && (
            <div className="calling-overlay">
              <div className="calling-content">
                <div className="calling-spinner">
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                </div>
                <h2 className="calling-title">Connecting to Avatar...</h2>
                <p className="calling-subtitle">Please allow microphone access when prompted by your browser</p>
              </div>
            </div>
          )}
          
          {statusMessage.text && !loading && (
            <div className={`status-notification ${statusMessage.isError ? 'error' : 'success'}`}>
                  <svg className="status-svg" viewBox="0 0 420 100" preserveAspectRatio="none">
      <defs>
        <linearGradient id={statusMessage.isError ? "errorGrad" : "successGrad"} x1="0%" y1="0%" x2="100%" y2="100%">
          {statusMessage.isError ? (
            <>
              <stop offset="0%" style={{ stopColor: 'rgba(239, 68, 68, 0.12)' }} />
              <stop offset="50%" style={{ stopColor: 'rgba(220, 38, 38, 0.08)' }} />
              <stop offset="100%" style={{ stopColor: 'rgba(255,255,255,0.02)' }} />
            </>
          ) : (
            <>
              <stop offset="0%" style={{ stopColor: 'rgba(34, 197, 94, 0.12)' }} />
              <stop offset="50%" style={{ stopColor: 'rgba(22, 163, 74, 0.08)' }} />
              <stop offset="100%" style={{ stopColor: 'rgba(255,255,255,0.02)' }} />
            </>
          )}
        </linearGradient>
      </defs>
      <path 
        d="M 0,50 C 0,0 0,0 50,0 L 370,0 C 420,0 420,0 420,50 C 420,100 420,100 370,100 L 50,100 C 0,100 0,100 0,50 Z" 
        fill={`url(#${statusMessage.isError ? "errorGrad" : "successGrad"})`} 
        stroke={statusMessage.isError ? "rgba(239, 68, 68, 0.3)" : "rgba(34, 197, 94, 0.3)"} 
        strokeWidth="1" 
        className="status-path glass-effect" 
      />
    </svg>
    
    <div className="status-content">
      <div className="status-icon">
        {statusMessage.isError ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
      </div>
      
      <div className="status-body">
        <div className="status-header">
          <div className="status-title">
          </div>
        </div>
        <div className="status-message">{statusMessage.text}</div>
        {!statusMessage.isError && (statusMessage.text.includes('Ready to start') || statusMessage.text.includes('Configuration')) && (
          <div className="status-meta">Setup complete • Click to begin</div>
        )}
      </div>
    </div>
            </div>
          )}
        </div>

        {/* Control Buttons - Now in main avatar container */}
        {connected && (
          <div className="avatar-controls">
            {/* Microphone Button */}
            <button 
              className={`control-btn1 mic-btn ${micActive ? 'active' : ''}`}
              onClick={toggleMicrophone}
              disabled={!connected}
              title={micActive ? 'Turn off microphone' : 'Turn on microphone'}
            >
              {micActive ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-3a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>

            {/* Clear Chat Button */}
            <button 
              className="control-btn1 clear-btn"
              onClick={clearChat}
              disabled={!connected || messages.length === 0}
              title="Clear chat history"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>

            {/* Stop Speaking Button - Only show when avatar is speaking */}
            {speaking && (
              <button 
                className="control-btn1 stop-btn"
                onClick={stopSpeaking}
                title="Stop speaking"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h6v4H9z" />
                </svg>
              </button>
            )}

            {/* End Session Button */}
            <button 
              className="control-btn1 end-btn"
              onClick={endSession}
              title="End session"
            >
<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.18-.29-.43-.29-.71 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.66c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
</svg>
            </button>
          </div>
        )}

        {/* Chat Toggle Button */}
        {connected && (
          <button 
            className="chat-toggle-button" 
            onClick={toggleChat}
            aria-label="Toggle chat"
          >
            {chatOpen ? (
              '×'
            ) : (
              <svg
                className="w-6 h-6 text-gray-800 dark:text-white"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M3 5.983C3 4.888 3.895 4 5 4h14c1.105 0 2 .888 2 1.983v8.923a1.992 1.992 0 0 1-2 1.983h-6.6l-2.867 2.7c-.955.899-2.533.228-2.533-1.08v-1.62H5c-1.105 0-2-.888-2-1.983V5.983Zm5.706 3.809a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Zm2.585.002a1 1 0 1 1 .003 1.414 1 1 0 0 1-.003-1.414Zm5.415-.002a1 1 0 1 0-1.412 1.417 1 1 0 1 0 1.412-1.417Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        )}

        {/* Slidable Chat Container */}
        <div className={`chat-container ${chatOpen ? 'open' : ''}`}>
          <div className="chat-header">
            <h3>Chat with Avatar</h3>
            <button className="close-chat-button" onClick={toggleChat}>×</button>
          </div>
          
          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`message ${msg.role}`}
              >
                <div className="message-header">
                  {msg.role === 'user' ? 'You' : 'Avatar'}
                </div>
                <div className="message-content">
                  {msg.content}
                  {msg.hasImage && <div className="image-indicator">[Image attached]</div>}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="chat-input-form">
            <div className="input-container">
              <input
                type="text"
                value={userInput}
                onChange={handleInputChange}
                placeholder={connected ? "Type your message..." : "Connecting..."}
                disabled={!connected || micActive}
              />
              
              <button 
                type="submit" 
                disabled={!connected || !userInput.trim() || micActive}
                className="send-button"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      </div>
                  <Sidebar2
        isCollapsed={sidebarCollapsed}
        toggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        
      />
    </div>
  );
};

export default ConversationSession;